/**
 * /fb/success - post-payment thank-you for the Facebook funnel.
 *
 * Set the FB no-trial Stripe Payment Link's success/redirect URL to this page.
 * It is PUBLIC (no auth gate) because FB-funnel buyers are not logged in yet -
 * the passwordless account is created in the next phase. Its main jobs today:
 *   1. Fire the Meta "Purchase" conversion pixel (critical for ad optimization)
 *      + a PostHog payment_completed event, exactly once.
 *   2. Reassure the buyer and tell them what happens next (access by email).
 *
 * Purchase value/currency come from sessionStorage ('ttt_fb_purchase'), stashed
 * on the pricing page right before redirecting to Stripe. Server-side purchase
 * truth still comes from the Stripe webhook; this client pixel is for ads only.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check } from '@phosphor-icons/react';
import posthog from 'posthog-js';
import { trackPixel } from '@/lib/metaPixel';
import { useFbFunnelLang } from '@/lib/useFbFunnelLang';
import {
  useKeyboardInset,
  useScrollFocusedIntoView,
  keyboardFixedShellStyle,
} from '@/hooks/useKeyboardInset';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { API_BASE_URL } from '@/lib/apiBaseUrl';
import { FB, FbButton } from '@/pages/FbOnboarding/components/FbShell';
import { LANDING_ROUTE } from '@/lib/landingRoute';

const PW_KEY = 'ttt_fb_pw';

export default function FbSuccess() {
  useFbFunnelLang();
  const { user, checkSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('your child');
  // idle | sending | code | verifying | verify_error | send_error | entering
  const [authStep, setAuthStep] = useState('idle');
  const [signedIn, setSignedIn] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const pendingTargetRef = useRef(LANDING_ROUTE);
  const firedRef = useRef(false);

  const readStashedEmail = useCallback(() => {
    try {
      const a = JSON.parse(sessionStorage.getItem('ttt_fb_answers') || '{}');
      return (user?.email || a.email || '').trim().toLowerCase();
    } catch {
      return (user?.email || '').trim().toLowerCase();
    }
  }, [user?.email]);

  const clearFbSecrets = () => {
    try {
      sessionStorage.removeItem(PW_KEY);
    } catch {
      /* ignore */
    }
  };

  // Stripe redirects here with ?session_id={CHECKOUT_SESSION_ID} (set on the
  // Payment Link). The backend verifies the checkout is PAID with Stripe and
  // mints a one-time login token for the payer's email - no email round-trip,
  // and verifyOtp() redeems it in THIS browser, so it works inside the FB
  // webview where Google is blocked and emailed links open elsewhere.
  const checkoutAuth = useCallback(async () => {
    let sessionId = '';
    try {
      sessionId = new URLSearchParams(window.location.search).get('session_id') || '';
    } catch {
      return null;
    }
    if (!sessionId) return null;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/fb/checkout-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      if (!resp.ok) return null;
      const { email: payerEmail, token_hash } = await resp.json();
      if (!token_hash) return null;
      const { data, error } = await supabase.auth.verifyOtp({ type: 'email', token_hash });
      if (error || !data?.session) return null;
      if (payerEmail) setEmail(payerEmail);
      clearFbSecrets();
      return data.session;
    } catch {
      return null;
    }
  }, []);

  // Re-establish the Supabase session in THIS browser tab. Order: existing
  // session -> paid-checkout auto-login (works for ANY payer, even a returning
  // email) -> the generated signup password stashed before the redirect.
  // The emailed OTP code is the last resort when all of these fail.
  const establishSession = useCallback(async (emailAddr) => {
    const normalized = (emailAddr || '').trim().toLowerCase();

    try {
      const { data: existing } = await supabase.auth.getSession();
      if (existing?.session) return existing.session;
    } catch {
      /* fall through */
    }

    const fromCheckout = await checkoutAuth();
    if (fromCheckout) return fromCheckout;

    try {
      const pw = sessionStorage.getItem(PW_KEY);
      if (pw && normalized) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: normalized,
          password: pw,
        });
        if (!error && data?.session) {
          clearFbSecrets();
          return data.session;
        }
      }
    } catch {
      /* fall through */
    }

    return null;
  }, [checkoutAuth]);

  // /home requires an active subscription row. After Payment Link checkout the
  // webhook can lag; mirror Success.jsx and pull the sub from Stripe directly.
  const restoreSubscriptionFromStripe = async () => {
    const attempts = 4;
    const delayMs = 1500;
    for (let i = 0; i < attempts; i++) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }
        const resp = await fetch(`${API_BASE_URL}/api/stripe/restore`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (resp.ok) {
          const result = await resp.json();
          if (result?.hasActiveSubscription) {
            localStorage.setItem('ttt_has_plan', 'premium');
            if (checkSubscriptionStatus) {
              await checkSubscriptionStatus();
            }
            return true;
          }
        }
      } catch {
        /* retry */
      }
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    return false;
  };

  const enterApp = async (target) => {
    if (target === LANDING_ROUTE || target === '/home' || target.startsWith('/home/')) {
      await restoreSubscriptionFromStripe();
    }
    // Hard navigation so AuthContext and subscription guards reload with the
    // session we just established (client-side navigate races ProtectedRoute).
    window.location.replace(target);
  };

  const goToApp = async (target) => {
    pendingTargetRef.current = target;
    const emailAddr = (email || readStashedEmail()).trim().toLowerCase();
    if (emailAddr && !email) setEmail(emailAddr);

    // Try the no-email paths first (existing session, paid-checkout token,
    // stashed password) - they need no address and cover almost everyone.
    setAuthStep('entering');
    const session = await establishSession(emailAddr);
    if (session) {
      setSignedIn(true);
      await enterApp(target);
      return;
    }

    // Emailed code is the last resort and the only step that needs an address.
    if (!emailAddr) {
      navigate('/login', { replace: true });
      return;
    }
    setAuthStep('sending');
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: emailAddr,
        options: { shouldCreateUser: false },
      });
      setAuthStep(error ? 'send_error' : 'code');
    } catch {
      setAuthStep('send_error');
    }
  };

  const handleVerifyCode = async () => {
    const token = otpCode.trim();
    const emailAddr = (email || readStashedEmail()).trim().toLowerCase();
    if (token.length < 6 || !emailAddr) return;
    setAuthStep('verifying');
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email: emailAddr, token, type: 'email' });
      if (error || !data?.session) {
        setAuthStep('verify_error');
        return;
      }
      clearFbSecrets();
      await enterApp(pendingTargetRef.current);
    } catch {
      setAuthStep('verify_error');
    }
  };

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    let purchase = { currency: 'EUR', price: '11.49' };
    try {
      const a = JSON.parse(sessionStorage.getItem('ttt_fb_answers') || '{}');
      const addr = (user?.email || a.email || '').trim().toLowerCase();
      if (addr) setEmail(addr);
      if (a.name && a.name.trim()) setName(a.name.trim());
      const p = JSON.parse(sessionStorage.getItem('ttt_fb_purchase') || 'null');
      if (p && p.price) purchase = p;
    } catch {
      /* fall back to defaults */
    }

    // Meta Purchase conversion (ad optimization) + PostHog. Best-effort.
    // The card is charged at checkout (no trial), so the honest browser event is
    // Purchase with the real amount. eventID = the Stripe session id from the
    // redirect URL, matching the event_id the webhook sends with its Purchase,
    // so Meta counts the browser + server pair as ONE sale.
    let stripeSessionId = '';
    try {
      stripeSessionId = new URLSearchParams(window.location.search).get('session_id') || '';
    } catch {
      /* no dedup id, still fire the pixel */
    }
    trackPixel('Purchase', { value: Number(purchase.price), currency: purchase.currency }, stripeSessionId);
    try {
      let consented = false;
      try {
        consented = localStorage.getItem('ttt_cookie_consent') === 'accepted';
      } catch {
        /* treat as no consent */
      }
      if (user?.email && consented) posthog.identify(user.email, { fb_funnel: true });
      // Canonical payment_completed now fires server-side from the Stripe webhook
      // (server/posthog-capture.js), so this is only a "saw the success screen"
      // signal. Do NOT rename back to payment_completed or FB sales double-count.
      posthog.capture('success_page_viewed', { funnel: 'fb', ...purchase });
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem('ttt_fb_purchase');
    } catch {
      /* ignore */
    }
  }, [user?.email]);

  // Silent re-auth on load so the first button tap works immediately. Runs
  // even without a stashed email: the paid-checkout path only needs the
  // session_id in the URL and recovers the email from Stripe.
  useEffect(() => {
    const addr = readStashedEmail();
    if (addr) setEmail(addr);
    void establishSession(addr).then((s) => {
      if (s) {
        setSignedIn(true);
        if (s.user?.email) setEmail(s.user.email);
      }
    });
  }, [readStashedEmail, establishSession]);

  const busy = authStep === 'sending' || authStep === 'entering' || authStep === 'verifying';

  // The 6-digit code field autofocuses, so the keyboard is up the moment this
  // screen falls back to the emailed code. This shell was `fixed inset-0` with
  // no scroller, so on a 375x812 phone the field and the Confirm button below
  // it sat under the keyboard, on the screen a customer reaches AFTER paying.
  // Shrinking the shell to the visible region is what makes the scroll area
  // below reachable at all.
  const keyboardInset = useKeyboardInset();
  useScrollFocusedIntoView(keyboardInset);

  return (
    <div
      className="fb-funnel fixed inset-0 flex flex-col overflow-y-auto overscroll-contain text-center px-8"
      style={{
        backgroundColor: FB.cream,
        fontFamily: FB.font,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        ...keyboardFixedShellStyle(keyboardInset),
      }}
    >
      <Helmet>
        <title>Timo Helps</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* `m-auto`, not `justify-center`: auto margins centre the column when
          there is room and collapse to zero when there is not, so the top of
          the content stays scrollable. Centring a flex column that overflows
          makes the overflow unreachable. Same pattern as /scratch. */}
      <div className="m-auto flex w-full flex-col items-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: FB.orange }}
      >
        <Check size={40} weight="bold" color="#fff" />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35 }}
        className="font-semibold text-[26px] mb-2"
        style={{ color: FB.ink }}
      >
        You're all set 🎉
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.35 }}
        className="text-[16px] max-w-xs mb-1"
        style={{ color: '#6B6472' }}
      >
        Payment received. Timo is getting {name}'s plan ready.
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.35 }}
        className="text-[14px] max-w-xs"
        style={{ color: '#9A93A2' }}
      >
        {signedIn ? (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium"
            style={{ backgroundColor: '#E2F1EA', color: '#2E7D5B' }}
          >
            <Check size={14} weight="bold" />
            Signed in{email ? ` as ${email}` : ''}
          </span>
        ) : email ? (
          <>Your account is <span className="font-medium" style={{ color: FB.ink }}>{email}</span>.</>
        ) : (
          'Your account is ready.'
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.35 }}
        className="w-full max-w-xs mt-8"
      >
        {authStep === 'code' || authStep === 'verifying' || authStep === 'verify_error' ? (
          <>
            <p className="text-[14px] leading-snug mb-3" style={{ color: FB.ink }}>
              We've sent a 6-digit code to <span className="font-medium">{email}</span>. Enter it below to get in.
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
              placeholder="000000"
              className="w-full py-4 px-5 mb-3 rounded-[18px] text-[20px] font-semibold text-center tracking-[0.3em] bg-white border-[1.5px] border-transparent focus:outline-none focus:border-[#FF8C5A] shadow-[0_2px_10px_rgba(45,49,66,0.05)]"
              style={{ color: FB.ink }}
            />
            <FbButton
              onClick={handleVerifyCode}
              disabled={authStep === 'verifying' || otpCode.trim().length < 6}
            >
              {authStep === 'verifying' ? 'Checking...' : 'Confirm code'}
            </FbButton>
            {authStep === 'verify_error' && (
              <p className="mt-3 text-[13px] font-medium" style={{ color: '#E06A4A' }}>
                That code didn't work. Check your inbox for the latest one.
              </p>
            )}
          </>
        ) : (
          <>
            <FbButton
              onClick={() => {
                posthog.capture('fb_pricing_success_continue_tapped');
                void goToApp('/fb/onboarding/continue');
              }}
              disabled={busy}
            >
              {busy ? 'One sec...' : `Finish ${name}'s full plan`}
            </FbButton>
            <button
              type="button"
              onClick={() => {
                posthog.capture('fb_pricing_success_skip_tapped');
                void goToApp(LANDING_ROUTE);
              }}
              disabled={busy}
              className="w-full mt-3 min-h-[44px] py-2 text-[14px] font-medium touch-manipulation disabled:opacity-50"
              style={{ color: '#9A93A2' }}
            >
              Skip, take me to the app
            </button>
            {authStep === 'send_error' && (
              <p className="mt-3 text-[13px] font-medium" style={{ color: '#E06A4A' }}>
                Couldn't send the code. Try again in a moment.
              </p>
            )}
          </>
        )}
      </motion.div>
      </div>
    </div>
  );
}
